/**
 * Expands a spec's explanation inline, beneath the three-fact row.
 *
 * Extends HTMLElement rather than Component: there are no refs to track, and
 * the accordion custom element does the same for the same reason.
 *
 * Nothing here measures or positions anything. The panel's height comes from
 * a 0fr -> 1fr grid row in CSS, so it always lands on however tall the copy
 * actually is — the replacement for a floating popover that had to compute
 * its own placement and twice got it wrong.
 *
 * The notes are collapsed in the stylesheet unconditionally, not gated
 * behind an attribute this element adds — that was an earlier version's
 * approach, meant to keep the copy readable if JS ever failed to load, and
 * it caused a real layout shift on every single page view instead: three
 * panels open, then visibly animating shut a moment after paint. A toggle
 * that simply does nothing without JS is the correct trade here.
 */
class SpecInfoNotes extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    // The stylesheet already renders every note collapsed; this only takes
    // it out of the accessibility tree and tab order to match — otherwise a
    // screen reader reads all three explanations straight through as though
    // they were open, and Tab lands on text nobody can see.
    for (const note of this.querySelectorAll('.product-spec-highlights__note')) {
      note.inert = true;
    }

    this.addEventListener('click', this.#handleClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    const toggle = target.closest?.('.product-spec-highlights__toggle');

    if (!(toggle instanceof HTMLElement)) return;

    const opening = toggle.getAttribute('aria-expanded') !== 'true';

    // One at a time: two open notes would push the buy button around more
    // than the answer is worth, and the row reads as a set of alternatives.
    for (const other of this.querySelectorAll('.product-spec-highlights__toggle')) {
      this.#setOpen(other, other === toggle && opening);
    }
  };

  /**
   * @param {Element} toggle
   * @param {boolean} open
   */
  #setOpen = (toggle, open) => {
    const id = toggle.getAttribute('aria-controls');
    const note = id && this.querySelector(`#${CSS.escape(id)}`);

    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (!(note instanceof HTMLElement)) return;

    note.toggleAttribute('data-open', open);
    note.inert = !open;
  };
}

if (!customElements.get('spec-info-notes')) {
  customElements.define('spec-info-notes', SpecInfoNotes);
}
